import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import { exportRowsToExcel } from '../utils/exportExcel';
import './VarianceReport.css';

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const FailedPickReport = () => {
  const navigate = useNavigate();
  const { fetchWithAuth } = useAuth();
  const [items, setItems] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [days, setDays] = useState('30');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const d = Math.min(365, Math.max(1, parseInt(days, 10) || 30));
      const url = `/api/pick-lists/reports/failed-line-picks?days=${encodeURIComponent(d)}&limit=${encodeURIComponent(2000)}`;
      const response = await fetchWithAuth(url);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || 'Failed to load failed pick lines');
      }
      const data = await response.json();
      setItems(Array.isArray(data.items) ? data.items : []);
      setGeneratedAt(data.generatedAt || null);
    } catch (err) {
      setError(err.message || 'Failed to load report');
      setItems([]);
      setGeneratedAt(null);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, days]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((row) => {
      const pack = [
        row.pickListId,
        row.obdNumber,
        row.orderNumber,
        row.customerNumber,
        row.pickListStatus,
        row.partNumber,
        row.partDescription,
        row.pickLineResult,
        row.failureReason,
      ]
        .filter((x) => x != null && x !== '')
        .join(' ')
        .toLowerCase();
      return pack.includes(q);
    });
  }, [items, query]);

  const formatResult = (r) => {
    const x = String(r || '').toLowerCase();
    if (x === 'unavailable') return 'Not available';
    if (x === 'short') return 'Short';
    return r || '—';
  };

  const handleExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const rows = filtered.map((row) => ({
      'Pick list': row.pickListId != null ? String(row.pickListId) : '',
      'List status': row.pickListStatus || '',
      'List updated': row.pickListUpdatedAt ? new Date(row.pickListUpdatedAt).toLocaleString() : '',
      OBD: row.obdNumber || '',
      Order: row.orderNumber || '',
      Customer: row.customerNumber || '',
      'Part number': row.partNumber || '',
      Description: row.partDescription || '',
      Ordered: row.orderedQty != null ? String(row.orderedQty) : '',
      Picked: row.pickedQty != null ? String(row.pickedQty) : '',
      Result: formatResult(row.pickLineResult),
      Reason: row.failureReason || '',
    }));
    exportRowsToExcel(`failed-pick-lines-${stamp}`, 'Failed lines', rows);
  };

  return (
    <Layout>
      <div className="variance-container">
        <div className="variance-header">
          <div className="variance-header-left">
            <button type="button" className="variance-back" onClick={() => navigate('/reports')}>
              ← Back to Reports
            </button>
            <h1>Failed pick lines</h1>
          </div>
          <div className="variance-header-actions">
            <label className="variance-filter">
              <span className="variance-filter-label">Days:</span>
              <select
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="variance-filter-select"
              >
                {[7, 14, 30, 60, 90, 180, 365].map((n) => (
                  <option key={n} value={String(n)}>
                    Last {n} days
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="variance-refresh" onClick={load}>
              Refresh
            </button>
            <button type="button" className="variance-refresh" onClick={handleExport} disabled={filtered.length === 0}>
              ⬇ Excel
            </button>
          </div>
        </div>
        <p className="variance-subtitle">
          Lines marked short pick or not available. Open pick lists with not-available lines stay active until an admin clears them from{' '}
          <strong>Pick list → Reopen for picker</strong> or the picker updates the line.
          {generatedAt ? ` Generated at ${formatDate(generatedAt)}.` : ''}
        </p>
        <div style={{ marginBottom: 12, maxWidth: 420 }}>
          <label className="variance-filter" style={{ width: '100%' }}>
            <span className="variance-filter-label">Filter:</span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Pick list, part, customer…"
              className="variance-filter-select"
              style={{ flex: 1, minWidth: 0 }}
            />
          </label>
        </div>
        {error && <div className="variance-error">{error}</div>}
        {isLoading ? (
          <div className="variance-loading">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="variance-loading">No matching rows.</div>
        ) : (
          <div className="variance-table-wrap">
            <table className="variance-table">
              <thead>
                <tr>
                  <th>Pick list</th>
                  <th>List status</th>
                  <th>Updated</th>
                  <th>OBD</th>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Part</th>
                  <th>Description</th>
                  <th>Ordered</th>
                  <th>Picked</th>
                  <th>Result</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, idx) => (
                  <tr key={`${row.pickListId}-${row.lineId}-${idx}`}>
                    <td>{row.pickListId}</td>
                    <td>{row.pickListStatus || '—'}</td>
                    <td>{formatDate(row.pickListUpdatedAt)}</td>
                    <td>{row.obdNumber || '—'}</td>
                    <td>{row.orderNumber || '—'}</td>
                    <td>{row.customerNumber || '—'}</td>
                    <td>{row.partNumber || '—'}</td>
                    <td>{row.partDescription || '—'}</td>
                    <td>{row.orderedQty != null ? row.orderedQty : '—'}</td>
                    <td>{row.pickedQty != null ? row.pickedQty : '—'}</td>
                    <td>{formatResult(row.pickLineResult)}</td>
                    <td>{row.failureReason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FailedPickReport;
